/**
 * Mirror of prior-auth-service Config/X12/Transactions/278/FhirMappings.json.
 * Keep in sync when backend mapping rules change.
 */
export const FHIR_MAPPINGS_CONFIG = {
  description:
    "FHIR PAS Bundle → PriorAuthRequest field bindings (ConfigPasMapper / PasBundleMapper).",
  version: "1.0",
  mappings: [
    {
      target: "TrackingNumber",
      fhirPath: "Bundle.entry.resource.where(resourceType='Claim').first().id",
      transform: "deriveTrackingNumber",
      notes: "Reuses Claim.id verbatim; mints PAS-{guid} when absent.",
    },
    {
      target: "GeneratedAt",
      transform: "utcNow",
      notes: "Set at map time; not sourced from FHIR.",
    },
    {
      target: "Payer.Name",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='Coverage').first().payor.first().display",
      fallbackPath:
        "Bundle.entry.resource.where(resourceType='Organization').where(identifier.exists() or name.exists()).first().name",
      notes: "Resolved Organization.name when payor is a reference.",
    },
    {
      target: "Payer.PayerId",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='Coverage').first().class.where(type.coding.code='plan').first().value",
      fallbackPath:
        "Bundle.entry.resource.where(resourceType='Coverage').first().identifier.first().value",
    },
    {
      target: "Payer.IdentifierCode",
      transform: "resolvePayerIdentifier",
      notes: "Organization.identifier.system → PI/XX/24/46/XV.",
    },
    {
      target: "Requester",
      transform: "buildRequester",
      notes:
        "Claim.provider → Practitioner (NM102=1) or Organization (NM102=2). Taxonomy from claim-scope careTeam.qualification.",
    },
    {
      target: "Subscriber",
      transform: "buildSubscriber",
      notes:
        "Coverage.subscriber + subscriberId. Patient = subscriber when self or unique member ID.",
    },
    {
      target: "Dependent",
      transform: "buildDependent",
      notes: "Loop 2000D when patient ≠ subscriber without distinct member ID.",
    },
    {
      target: "Priority",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='Claim').first().priority.coding.first().code",
      transform: "mapPriorAuthPriority",
    },
    {
      target: "HasAttachments",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='DocumentReference').exists() or Claim.supportingInfo DocumentReference ref",
      transform: "bool",
    },
    {
      target: "CertificationType",
      fhirPath:
        "Claim.item.where(productOrService absent).category.coding[CertificationTypeCS].code",
      notes:
        "Event UM01: non-service Claim.item CertificationTypeCS only; default HS. Service-line category → 2000F.",
    },
    {
      target: "PlaceOfService",
      transform: "extractPlaceOfService",
      fhirPath: "Claim.facility → Location.type[CMS POS]",
      notes:
        "Event UM04: Claim.facility Location CMS POS only. Not Claim.item.location.",
    },
    {
      target: "AdmissionType",
      transform: "extractSupportingInfoCode",
      supportingInfoCategory: "admissiontype",
      notes: "supportingInfo.code.coding (AdmissionTypeCS).",
    },
    {
      target: "BhtPurposeCode",
      transform: "mapBhtPurpose",
      fhirPath: "Claim.status",
      notes: "cancelled/entered-in-error → Cancellation; else Request.",
    },
    {
      target: "BhtTransactionTypeCode",
      transform: "extractBhtTransactionType",
      supportingInfoCategory: "requestType",
      notes: "supportingInfo.code coding RU or Reservation text → BHT06.",
    },
    {
      target: "ClaimType",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='Claim').first().type.coding.first().code",
      transform: "mapClaimType",
    },
    {
      target: "CertificationAction",
      transform: "extractCertificationAction",
      supportingInfoCategory: "certificationAction",
      notes: "supportingInfo.code coding I/R/S/3/4/1 or UM02 text hints.",
    },
    {
      target: "OriginalAuthorizationNumber",
      fhirPath:
        "Bundle.entry.resource.where(resourceType='Claim').first().insurance.preAuthRef.first()",
    },
    {
      target: "LengthOfStayDays",
      transform: "extractLengthOfStayDays",
      supportingInfoCategories: ["lengthOfStay", "additionalLengthOfStay"],
      notes: "HSD*DY from supportingInfo — not Claim.item.quantity.",
    },
    {
      target: "FacilityName",
      transform: "resolveFacilityField",
      fhirPath: "Claim.facility → Location.name",
    },
    {
      target: "Frequency",
      transform: "extractFrequency",
      supportingInfoCategory: "frequency",
    },
    {
      target: "CertificationPeriod",
      transform: "extractCertificationPeriod",
      supportingInfoCategory: "certificationperiod",
    },
    {
      target: "Messages",
      transform: "extractMessages",
      supportingInfoCategories: [
        "additionalInformation",
        "message",
        "note",
        "remarks",
      ],
    },
  ],
  collections: [
    {
      target: "Services",
      fhirPath: "Claim.item",
      itemMappings: [
        {
          target: "ProcedureCode",
          fhirPath: "productOrService.coding.first().code",
        },
        {
          target: "QuantityRequested",
          fhirPath: "quantity.value",
        },
        {
          target: "MonetaryAmount.net",
          fhirPath: "net.value",
        },
        {
          target: "MonetaryAmount.unitPrice",
          fhirPath: "unitPrice.value",
        },
        {
          target: "ServiceFromDate",
          fhirPath: "serviced / servicedPeriod.start",
          transform: "servicedStartDate",
        },
        {
          target: "PlaceOfService",
          fhirPath: "locationCodeableConcept.coding.first().code",
        },
      ],
    },
    {
      target: "Diagnoses",
      fhirPath: "Claim.diagnosis",
      itemMappings: [
        {
          target: "Code",
          fhirPath:
            "diagnosisCodeableConcept.coding.where(system contains 'icd-10-cm').first().code",
        },
      ],
    },
  ],
};
